import type {
  NetworkConnectionListItem,
  NetworkDomainHostEffectiveDecision,
  NetworkDomainHostEffectiveSource,
} from '@kirin/api-client/contracts/shadow-ai';
import type { NetworkStatusFilterValue } from './NetworkStatusFilterSelector';

/**
 * Whether this row is a catalog SERVICE rather than a single host.
 *
 * `row.grouped` is the server's own statement about the grain it answered in,
 * and it wins whenever it is present. `hostCount > 1` is the guess the UI made
 * before that field existed, kept only for the rollout window: grouping ships
 * on the previous revision too, so an API one deploy behind serves grouped
 * rows without the field, and reading its silence as "not grouped" would
 * collapse every one of those services into a single host.
 *
 * The guess is wrong in both directions, which is why the field replaced it:
 * `hostCount` counts the hosts THIS VIEWER's events reach, so a service with
 * one visible host read as ungrouped. It is still strictly better than
 * assuming false while the two revisions overlap.
 *
 * Takes only the two fields it reads, like `isOverriddenHost` below, so a
 * caller -- a test especially -- can hand it those two without a cast that
 * would hide a field going missing.
 *
 * Shared so the status pill and the expansion cannot disagree about whether a
 * row groups. Every row here expands (no `canExpand` is passed), so what the
 * pill needs is not "does the row open" but "does it open onto a HOST TABLE" —
 * only a grouped row renders one.
 */
export function isGroupedRow(
  connection: Pick<NetworkConnectionListItem, 'grouped' | 'hostCount'>
): boolean {
  return connection.grouped ?? connection.hostCount > 1;
}

/**
 * Whether a REVIEWER singled this host out, as opposed to a domain-level action
 * having mirrored its answer down onto the host.
 *
 * The distinction is the whole point of the per-host column, and `decision`
 * cannot carry it. Every domain-level Identify/Dismiss fans out through
 * `applyHostDecision`, which writes each member host's `decision` and leaves
 * `decision_source` NULL — so immediately after classifying a service, every
 * one of its hosts has a non-null `decision` it never chose. Keying the badge,
 * the panel copy and the Status menu off `decision` therefore announced that
 * the entire service dissents at exactly the moment nothing did, and buried the
 * hosts that genuinely disagree.
 *
 * Only `setHostDecision` sets `decision_source = 'manual'`, and that is the
 * same set `applyHostDecision` skips — so `overridden` is literally the
 * "a domain-level action will not change it" the badge claims.
 *
 * The `??` keeps the old guess for the rollout window only: `overridden` is
 * optional in the contract because the frontend and API deploy separately, and
 * a new client meeting an older API gets `undefined`. Falling back to the old
 * reading is wrong in the same way it has always been wrong, which beats
 * defaulting to `false` and hiding overrides that are real.
 */
export function isOverriddenHost(host: {
  decision: string | null;
  overridden?: boolean;
}): boolean {
  return host.overridden ?? host.decision !== null;
}

/**
 * What decided the status a host shows (KIR-5168): its own override, its
 * domain, or Kirin's built-in lists. Null while the host is unreviewed.
 *
 * The server answers this in `effectiveSource`. The fallback is for an older
 * API that does not send it, which also knows nothing of built-in hosts: there
 * a decided host is either one a reviewer overrode or one following its domain,
 * the same reading the overridden chip used before the field existed.
 */
export function hostDecisionSource(host: {
  effectiveDecision: NetworkDomainHostEffectiveDecision;
  effectiveSource?: NetworkDomainHostEffectiveSource | null;
  decision: string | null;
  overridden?: boolean;
}): NetworkDomainHostEffectiveSource | null {
  if (host.effectiveSource !== undefined) return host.effectiveSource;
  if (host.effectiveDecision === 'unreviewed') return null;
  return isOverriddenHost(host) ? 'override' : 'domain';
}

/** The host decision each status scope is about; `all` is about none. */
const SCOPE_DECISION: Record<
  NetworkStatusFilterValue,
  NetworkDomainHostEffectiveDecision | null
> = {
  pending: 'unreviewed',
  approved: 'identified',
  ignored: 'dismissed',
  all: null,
};

/**
 * The hosts of an expanded row, with the ones the current tab is about first
 * (KIR-5168).
 *
 * A grouped row lands on the Identified tab when any one host is identified,
 * so the host that put it there can sit anywhere in a list of dozens ordered by
 * hit count. Every host stays -- the list is "all hosts seen" and the others
 * are why the row reads Mixed -- but the matching ones lead. Stable, so the
 * server's hit-count order holds within each group.
 */
export function orderHostsForScope<
  T extends { effectiveDecision: NetworkDomainHostEffectiveDecision },
>(hosts: readonly T[], scope: NetworkStatusFilterValue): T[] {
  const first = SCOPE_DECISION[scope];
  if (first === null) return [...hosts];
  return [
    ...hosts.filter(h => h.effectiveDecision === first),
    ...hosts.filter(h => h.effectiveDecision !== first),
  ];
}
